import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ChangeEvent, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../../../hooks/useAxiosHook';
import { LabelValue } from '../../../../../../../types/options';
import { invalidDecimalPointCharactersRegex } from '../../../../../../../utils/constants/regex';
import FieldWithErrors from '../../../../../../Forms/ReactHookForm/FieldWithErrors';
import {
  RedemptionFieldValidation,
  RedemptionReceiver,
  RedemptionReceiverFieldValidation,
  SpecialProduct,
  UnregisteredClientId,
  getRedemptionReceiverTooltip,
  validateRedemptionField,
} from '../../CreateEditDialog.functions';

type Props = {
  isRedemptionReceiverDisabled: boolean;
  redemptionReceiverFieldValidation: RedemptionReceiverFieldValidation;
};

export function PackageAttributes({
  isRedemptionReceiverDisabled,
  redemptionReceiverFieldValidation,
}: Props) {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const values = watch();

  const { data: specialProductData } = useAxiosHook<SpecialProduct[]>(
    '/specialized_products'
  );

  const specialProductOptions = specialProductData?.map((product) => {
    return { label: product.name, id: product.id };
  });

  const redemptionFieldValidation = useMemo(
    () => validateRedemptionField(values?.klient_od_id, values?.vrednost),
    [values]
  );

  const redemptionReceiverTooltip = useMemo(
    () => getRedemptionReceiverTooltip(t, redemptionReceiverFieldValidation),
    [redemptionReceiverFieldValidation, t]
  );

  const redemptionReceiverOptions = useMemo<
    LabelValue<RedemptionReceiver>[]
  >(() => {
    return [
      { label: t('Sender'), value: RedemptionReceiver.Sender },
      { label: t('Orderer'), value: RedemptionReceiver.Orderer },
    ];
  }, [t]);

  const isRedemptionDisabled =
    redemptionFieldValidation ===
      RedemptionFieldValidation.UnregisteredSender &&
    (values?.klient_do_id as any) != UnregisteredClientId.Recipient;

  return (
    <>
      <FieldWithErrors name="volumen" label={t('Volume')} className="p-col-6">
        <Controller
          name="volumen"
          render={({ field }) => (
            <InputText
              id="volumen"
              name="volumen"
              value={field.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(
                  e.target.value?.replace(
                    invalidDecimalPointCharactersRegex,
                    ''
                  )
                );
              }}
              data-cy="volumen"
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors name="otkup" label={t('Redemption')} className="p-col-6">
        <Controller
          name="otkup"
          render={({ field }) => (
            <InputText
              id="otkup"
              name="otkup"
              value={field.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(
                  e.target.value?.replace(
                    invalidDecimalPointCharactersRegex,
                    ''
                  )
                );
              }}
              data-cy="redemption"
              disabled={isRedemptionDisabled}
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="klient_otkup_id"
        label={t('Redemption receiver')}
        className="p-col-6"
      >
        <Controller
          name="otkup"
          render={({ field }) => (
            <div
              className="disabled-redemption-receiver-tooltip"
              data-pr-tooltip={redemptionReceiverTooltip}
            >
              <Dropdown
                name="klient_otkup_id"
                inputId="klient_otkup_id"
                options={redemptionReceiverOptions}
                disabled={isRedemptionReceiverDisabled}
                placeholder={t('Not applicable')}
                value={
                  isRedemptionReceiverDisabled
                    ? redemptionReceiverFieldValidation ===
                      RedemptionReceiverFieldValidation.NoThirdParyOrderer
                      ? RedemptionReceiver.Sender
                      : null
                    : values?.klient_otkup_id
                }
                onChange={(e) => field.onChange(e.value)}
              />
            </div>
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="specialized_product_id"
        label={t('Specialized product')}
        className="p-col-6"
      >
        <Controller
          name="specialized_product_id"
          render={({ field }) => (
            <Dropdown
              id="specialized_product_id"
              name="specialized_product_id"
              options={specialProductOptions}
              value={field.value}
              onChange={field.onChange}
              filter
              filterPlaceholder={t('Search')}
              showClear
              placeholder={t('Select')}
              className="data-cy-specialized_product_id"
            />
          )}
        />
      </FieldWithErrors>
    </>
  );
}
